import { Model } from 'app/model/backendmodel';
import { PolybaseSetting } from './../../../dataset/polybase-setting';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { SqlDWSink } from 'app/model/pipeline/copysink/sqldw.copysink';
import { Metamodel } from 'app/model/base/model';

import { TabularSinkAdapter } from 'app/model/adapters/activities/copysink/tabular.sink.adapter';
import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { CopySinkAdapter } from './copysink.adapter';
import { CopyCommandSetting, IColumnDefaultValue } from 'app/model/dataset/copy-command-setting';
import {
    copyMethodOptionValues,
    CopyMethodOptionValue
} from 'app/shared/components/copy-method-setting/copy-method-setting.const';
import { ParameterConfig, AdapterContext, SharedModel, IModel } from 'shell-models-lib';
import { toParameterObject, toParameterClass } from '../../linkedservices/parameter-config.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';

export const adapter = {
    type: '*',
    writeBatchTimeout: '*',
    writeBatchSize: '*',
    preCopyScript: '*',
    allowPolyBase: '*',
    polyBaseSettings: {
        rejectValue: '*',
        rejectType: '*',
        rejectSampleValue: '*',
        useTypeDefault: '*'
    },
    maxConcurrentConnections: '*',
    structure: '*',
    tableOption: '*',
    sqlWriterUseTableLock: '*',
    disableMetricsCollection: '*',
    allowCopyCommand: '*',
    copyCommandSettings: {
        defaultValues: '*',
        additionalOptions: '*'
    },
    writeBehavior: '*',
    upsertSettings: '*'
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlDWSink),
    asynchronous: true,
    adapts: adapter
})
export class SqlDWSinkAdapter extends CopySinkAdapter<SqlDWSink, Model.SqlDWSink> {
    protected model(): IModel {
        return SqlDWSink.MODEL;
    }

    protected override async _toResource(model: SqlDWSink): Promise<Model.SqlDWSink> {
        const result: Model.SqlDWSink = {
            type: this.model().name
        };
        return SqlDWSinkAdapter.toResourceInternal(model, result, this.context);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected static _upsertToResource(result: Model.SqlSink, model: SqlDWSink): void {
        // for copy-wizard generated activities, where upsertSettings parameterized as one
        const upsertSettings = ValueAdapter.toResource(model, SqlDWSink.UPSERT_SETTINGS);
        if (upsertSettings?.type === 'Expression') {
            result.upsertSettings = upsertSettings;
        } else {
            result.upsertSettings = {
                keys: ValueAdapter.toResource(model, SqlDWSink.WRITE_BEHAVIOR_KEYS),
                interimSchemaName: ValueAdapter.toResource(model, SqlDWSink.WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME)
            };
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static toResourceInternal(
        model: SqlDWSink,
        result: Model.SqlDWSink,
        context: AdapterContext
    ): Model.SqlDWSink {
        TabularSinkAdapter.toSinkResource(model, result);
        let isUpsert = false;
        if (model.copyMethod === copyMethodOptionValues.PolyBase) {
            result.allowPolyBase = ValueAdapter.toResource(model, SqlDWSink.ALLOW_POLYBASE);
            if (result.allowPolyBase && model.polybaseSetting != null) {
                result.polyBaseSettings = {
                    rejectValue: ValueAdapter.toResource(model.polybaseSetting, PolybaseSetting.REJECT_VALUE),
                    rejectType: model.polybaseSetting.rejectType,
                    rejectSampleValue:
                        model.polybaseSetting.rejectType === 'percentage'
                            ? ValueAdapter.toResource(model.polybaseSetting, PolybaseSetting.REJECT_SAMPLE_VALUE)
                            : undefined,
                    useTypeDefault: ValueAdapter.toResource(model.polybaseSetting, PolybaseSetting.USE_TYPE_DEFAULT)
                };
            }
        } else if (model.copyMethod === copyMethodOptionValues.CopyCommand) {
            result.allowCopyCommand = ValueAdapter.toResource(model, SqlDWSink.ALLOW_COPY_COMMAND);
            if (result.allowCopyCommand && model.copyCommandSettings != null) {
                result.copyCommandSettings = {};
                result.copyCommandSettings.additionalOptions = Array.isArray(
                    model.copyCommandSettings.additionalOptions
                )
                    ? toParameterObject(model.copyCommandSettings.additionalOptions)
                    : ValueAdapter.toResource(model.copyCommandSettings, CopyCommandSetting.ADDITIONAL_OPTIONS);
                result.copyCommandSettings.defaultValues = Array.isArray(model.copyCommandSettings.defaultValues)
                    ? !arrayIsNullOrEmpty(model.copyCommandSettings.defaultValues)
                        ? model.copyCommandSettings.defaultValues
                              .filter((result) => result)
                              .map((value: ParameterConfig) => ({
                                  columnName: value.name,
                                  defaultValue: value.value
                              }))
                        : undefined
                    : ValueAdapter.toResource(model.copyCommandSettings, CopyCommandSetting.DEFAULT_VALUES);
            }
        } else {
            result.writeBehavior = ValueAdapter.toResource(model, SqlDWSink.COPY_METHOD);
            const isExpressionWriteBehavior = SqlDWSink.COPY_METHOD.templateValue(model) != null;
            if (isExpressionWriteBehavior || model.copyMethod === copyMethodOptionValues.Upsert) {
                isUpsert = true;
                SqlDWSinkAdapter._upsertToResource(result, model);
            } else if (model.copyMethod === copyMethodOptionValues.SCD2) {
                SqlDWSinkAdapter._upsertToResource(result, model);
            }
        }

        if (isUpsert || model.copyMethod === copyMethodOptionValues.BulkInsert) {
            result.sqlWriterUseTableLock = ValueAdapter.toResource(model, SqlDWSink.SQL_WRITER_USE_TABLE_LOCK);
        }

        result.tableOption = ValueAdapter.toResource(model, SqlDWSink.TABLE_OPTION);
        result.disableMetricsCollection = ValueAdapter.toResource(model, SqlDWSink.DISABLE_METRICS_COLLECTION);
        CopySinkAdapter.toCommonCopySinkResource(model, result, context);
        return result;
    }

    protected override async _toModel(resource: Model.SqlDWSink, owner: Activity): Promise<SqlDWSink> {
        const result = Owned.create<SqlDWSink>(this.model(), owner);
        return SqlDWSinkAdapter.toModelInternal(resource, result);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static toModelInternal(resource: Model.SqlDWSink, result: SqlDWSink): SqlDWSink {
        TabularSinkAdapter.toSinkModel(resource, result);

        // clear default value for allowPolybase and allowCopyCommand
        result.allowPolyBase = undefined;
        result.allowCopyCommand = undefined;

        if (resource.allowPolyBase) {
            result.copyMethod = copyMethodOptionValues.PolyBase as CopyMethodOptionValue;
            ValueAdapter.toModel(result, SqlDWSink.ALLOW_POLYBASE, resource.allowPolyBase);
            if (resource.polyBaseSettings != null) {
                result.polybaseSetting = Owned.create<PolybaseSetting>(PolybaseSetting.MODEL, result);
                result.polybaseSetting.rejectType =
                    resource.polyBaseSettings.rejectType != null
                        ? resource.polyBaseSettings.rejectType.toLowerCase()
                        : 'value';
                if (
                    resource.polyBaseSettings.rejectValue !== undefined &&
                    resource.polyBaseSettings.rejectValue.type === 'Expression'
                ) {
                    ValueAdapter.toModel(
                        result.polybaseSetting,
                        PolybaseSetting.REJECT_VALUE,
                        resource.polyBaseSettings.rejectValue
                    );
                } else {
                    result.polybaseSetting.rejectValue =
                        resource.polyBaseSettings.rejectValue != null ? +resource.polyBaseSettings.rejectValue : 0;
                }
                if (result.polybaseSetting.rejectType === 'percentage') {
                    if (
                        resource.polyBaseSettings.rejectSampleValue !== undefined &&
                        resource.polyBaseSettings.rejectSampleValue.type === 'Expression'
                    ) {
                        ValueAdapter.toModel(
                            result.polybaseSetting,
                            PolybaseSetting.REJECT_SAMPLE_VALUE,
                            resource.polyBaseSettings.rejectSampleValue
                        );
                    } else {
                        result.polybaseSetting.rejectSampleValue =
                            resource.polyBaseSettings.rejectSampleValue != null
                                ? +resource.polyBaseSettings.rejectSampleValue
                                : undefined;
                    }
                }
                if (resource.polyBaseSettings.useTypeDefault == null) {
                    result.polybaseSetting.useTypeDefault = false; // default value of runtime is false
                } else {
                    ValueAdapter.toModel(
                        result.polybaseSetting,
                        PolybaseSetting.USE_TYPE_DEFAULT,
                        resource.polyBaseSettings.useTypeDefault
                    );
                }
            }
        } else if (resource.allowCopyCommand) {
            result.copyMethod = copyMethodOptionValues.CopyCommand as CopyMethodOptionValue;
            ValueAdapter.toModel(result, SqlDWSink.ALLOW_COPY_COMMAND, resource.allowCopyCommand);
            if (resource.copyCommandSettings != null) {
                result.copyCommandSettings = Owned.create<CopyCommandSetting>(CopyCommandSetting.MODEL, result);
                if (
                    resource.copyCommandSettings.additionalOptions &&
                    typeof resource.copyCommandSettings.additionalOptions === 'object' &&
                    resource.copyCommandSettings.additionalOptions.type !== 'Expression'
                ) {
                    result.copyCommandSettings.additionalOptions = toParameterClass(
                        resource.copyCommandSettings.additionalOptions,
                        result.copyCommandSettings
                    );
                } else {
                    ValueAdapter.toModel(
                        result.copyCommandSettings,
                        CopyCommandSetting.ADDITIONAL_OPTIONS,
                        resource.copyCommandSettings.additionalOptions
                    );
                }
                if (
                    resource.copyCommandSettings.defaultValues &&
                    Array.isArray(resource.copyCommandSettings.defaultValues)
                ) {
                    result.copyCommandSettings.defaultValues = resource.copyCommandSettings.defaultValues.map(
                        (setting: IColumnDefaultValue) => {
                            const config = new ParameterConfig();
                            config.name = setting.columnName;
                            config.value = setting.defaultValue;
                            return config;
                        }
                    );
                } else {
                    ValueAdapter.toModel(
                        result.copyCommandSettings,
                        CopyCommandSetting.DEFAULT_VALUES,
                        resource.copyCommandSettings.defaultValues
                    );
                }
            }
        } else if (resource.writeBehavior !== undefined) {
            ValueAdapter.toModel(result, SqlDWSink.COPY_METHOD, resource.writeBehavior);
            // for copy-wizard generated activities, where upsertSettings parameterized as one
            if (resource.writeBehavior !== copyMethodOptionValues.BulkInsert && resource.upsertSettings) {
                if ((resource.upsertSettings as SharedModel.ExpressionValue)?.type === 'Expression') {
                    ValueAdapter.toModel(result, SqlDWSink.UPSERT_SETTINGS, resource.upsertSettings);
                } else {
                    const upsertSettings = resource.upsertSettings as Model.WriteBehaviorUpsertOptions;
                    ValueAdapter.toModel(
                        result,
                        SqlDWSink.WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME,
                        upsertSettings.interimSchemaName
                    );
                    ValueAdapter.toModel(result, SqlDWSink.WRITE_BEHAVIOR_KEYS, upsertSettings.keys);
                }
            }
        } else {
            result.copyMethod = undefined;
        }

        ValueAdapter.toModel(result, SqlDWSink.TABLE_OPTION, resource.tableOption);
        if (resource.sqlWriterUseTableLock !== undefined) {
            ValueAdapter.toModel(result, SqlDWSink.SQL_WRITER_USE_TABLE_LOCK, resource.sqlWriterUseTableLock);
        } else {
            result.sqlWriterUseTableLock = undefined;
        }
        ValueAdapter.toModel(result, SqlDWSink.DISABLE_METRICS_COLLECTION, resource.disableMetricsCollection);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);
        return result;
    }
}
